import { isArray, isPlainObject } from "remeda";
import {
  AudioTrack as TwilioAudioTrack,
  LocalVideoTrack as TwilioLocalVideoTrack,
  RemoteTrackPublication as TwilioRemoteTrackPublication,
} from "twilio-video";
import { logger } from "../../datadog/logger";
import { memoize, memoizedDeserializerWithInitialValue } from "../../utils";
import { DEFAULT_VIDEO_SETTINGS } from "../core/constants";
import {
  LocalAudioDelegator,
  LocalVideoDelegator,
  RemoteAudioDelegator,
  RemoteMediaDelegator,
} from "../core/room/participant/media/MediaDelegator";
import { RemoteParticipant } from "../core/room/participant/RemoteParticipant";
import { parseIdentity } from "../core/room/participant/utils";
import { RoomDelegator } from "../core/room/RoomDelegator";
import {
  Constraint,
  LocalAudioContextAtom,
  LocalAVTrackContextAtom,
  LocalVideoContextAtom,
} from "../core/types";
import { TwilioLocalAudioDelegator } from "./room/participant/media/LocalAudioDelegator";
import { TwilioLocalVideoDelegator } from "./room/participant/media/LocalVideoDelegator";
import { TwilioRemoteAudioDelegator } from "./room/participant/media/RemoteAudioDelegator";
import { TwilioRemoteVideoDelegator } from "./room/participant/media/RemoteVideoDelegator";

export function roomToDiagnosticInfo(room: RoomDelegator) {
  const { localParticipant, sid, name } = room;
  const { id, userType } = parseIdentity(localParticipant?.identity ?? "");
  return {
    localParticipantSid: localParticipant?.sid,
    localUserId: id,
    localUserType: userType,
    roomSid: sid,
    roomName: name,
  };
}

export function publicationToDiagnosticInfo(
  publication: TwilioRemoteTrackPublication,
) {
  return {
    trackName: publication.trackName,
    mediaKind: publication.kind,
    isSubscribed: publication.isSubscribed,
    isTrackEnabled: publication.isTrackEnabled,
    ...(publication.track ? { trackInfo: null } : { trackInfo: null }),
  };
}

export function remoteMediaDelagatorToDiagnosticInfo(
  remoteMediaDelegator: RemoteMediaDelegator,
) {
  return {
    mediaSid: remoteMediaDelegator.sid,
    mediaSource:
      "source" in remoteMediaDelegator
        ? remoteMediaDelegator.source
        : undefined,
    ...(remoteMediaDelegator.kind === "video"
      ? {
          trackDimensions:
            remoteMediaDelegator instanceof TwilioRemoteVideoDelegator
              ? remoteMediaDelegator.track?.dimensions
              : {
                  width: null,
                  height: null,
                },
        }
      : {}),
  };
}

const OGWebSocket = WebSocket;

class FallbackWebsocket extends OGWebSocket {
  constructor(givenUrl: string | URL, protocols?: string | string[]) {
    const wsFallbackUrlMap: Record<string, string> = {
      "wss://tsock.us1.twilio.com/v3/wsconnect": `wss://${window.location.hostname}/wss`,
    };
    const url = wsFallbackUrlMap[givenUrl.toString()] || givenUrl;

    super(url, protocols);

    this.addEventListener("open", (event) =>
      logger.debug("websocket open", event),
    );
    this.addEventListener("close", (event) =>
      logger.debug("websocket close", event),
    );
    this.addEventListener("error", (event) => {
      logger.warn("websocket error", event);
    });
  }
}

export const reverseProxyWebsockets = memoize(() => {
  logger.info("Using reverse proxy for websockets");
  window.WebSocket = FallbackWebsocket;
});

export function exitPictureInPicture() {
  if (document.pictureInPictureElement) {
    document.exitPictureInPicture();
  }
}

/**
 * Extracts a single value from a constraints object. **Do not use this with
 * multi-value constraint arrays, as this function will only return the first
 * value**.
 */
export function constraintsToValue<ReturnType>(
  constraint: Constraint,
  { shouldLog }: { shouldLog?: boolean } = { shouldLog: true },
) {
  let value;
  const handleArray = (list: Array<string>) => {
    if (list.length > 1 && shouldLog) {
      logger.warn("constraintsToValue called with multi-value array", {
        constraint,
      });
    }
    return list[0];
  };
  if (isArray(constraint)) {
    value = handleArray(constraint);
  } else if (isPlainObject(constraint)) {
    if (isArray(constraint.exact)) {
      value = handleArray(constraint.exact as string[]);
    } else if (isArray(constraint.ideal)) {
      value = handleArray(constraint.ideal as string[]);
    } else {
      value = constraint.exact ?? constraint.ideal;
    }
  } else {
    value = constraint;
  }

  return value as ReturnType;
}

export function hasParticipant(
  participants: Array<RemoteParticipant>,
  participant: RemoteParticipant,
) {
  return participants.some((p) => p.identity === participant.identity);
}

export const deserializerToEnsureObjectEquality =
  memoizedDeserializerWithInitialValue(DEFAULT_VIDEO_SETTINGS, (error) => {
    logger.error("JSON parse error", undefined, error);
  });

export const getTwilioTrackFromDelegator = (
  delegator: LocalAudioDelegator | LocalVideoDelegator | undefined,
) => {
  if (!delegator) {
    return undefined;
  }

  if (
    delegator instanceof TwilioLocalAudioDelegator ||
    delegator instanceof TwilioLocalVideoDelegator
  ) {
    return delegator.track;
  }

  throw new Error(
    "Delegator is not a TwilioLocalAudioDelegator or TwilioLocalVideoDelegator",
  );
};

export const transformToTwilioContext = ({
  delegator,
  status,
}: LocalAudioContextAtom | LocalVideoContextAtom) => {
  return {
    status,
    track: delegator ? getTwilioTrackFromDelegator(delegator) : undefined,
  };
};

export const transformToStandardContext = ({
  track,
  status,
}: LocalAVTrackContextAtom): LocalVideoContextAtom => {
  return {
    status,
    delegator: track
      ? new TwilioLocalVideoDelegator(track as TwilioLocalVideoTrack)
      : undefined,
  };
};

export function toTwilioAudioDelegatorTracks(
  delegators: NonNullable<
    LocalAudioDelegator | RemoteAudioDelegator | undefined
  >[],
): TwilioAudioTrack[] {
  return delegators
    .filter(
      (delegator) =>
        delegator instanceof TwilioLocalAudioDelegator ||
        delegator instanceof TwilioRemoteAudioDelegator,
    )
    .map((delegator) => delegator.track);
}
